import * as combinators from './combinators';
import { DecodingError } from './errors';
import { Result } from './result';

/** Represents the result of decoding an unknown value. */
export type DecodeResult<Output> = Result<Output, DecodingError>;

/** A function that decodes an unknown value. */
type DecodeFn<Output> = (value: unknown) => DecodeResult<Output>;

/** A decoder is a function that takes an unknown value as value and either produces a value of type `A` or a list of errors. */
export class Decoder<Output> {
    private constructor(readonly decode: DecodeFn<Output>) {}

    /** Creates a decoder instance from a function that decodes an unknown value. */
    static readonly create = <Output>(decode: DecodeFn<Output>) => new Decoder(decode);

    /** Creates a decoder that always succeeds with the provided value. */
    static readonly pure = <Output>(value: Output) => Decoder.create(() => Result.ok(value));

    /** Creates a decoder that always fails with the provided error. */
    static readonly fail = <Output = never>(message: string) =>
        Decoder.create<Output>(() => Result.error(DecodingError.errorMessage(message)));

    /** Maps the result of a decoder. */
    map<B>(f: (value: Output) => B): Decoder<B> {
        return combinators.map(this, f);
    }

    /** Extends the current decoder, using the result it produces to create another decoder which is then applied to the value in sequence. */
    flatMap<B>(f: (value: Output) => Decoder<B>): Decoder<B> {
        return combinators.bind(this, f);
    }

    /** Extends the current decoder so that both are tried in sequence, succeeding if either succeeds. */
    or<B>(decoder: Decoder<B>): Decoder<Output | B> {
        return combinators.alternative(this, decoder);
    }

    /** Extends the current decoder to also succeed if the value is `undefined`. */
    optional(): Decoder<Output | undefined> {
        return combinators.optional(this);
    }

    /** Extends the current decoder to also succeed if the value is `null`. */
    nullable(): Decoder<Output | null> {
        return combinators.nullable(this);
    }

    /** If the decode fails because the value is `undefined`, instead succeed with the provided value. */
    default(value: Output): Decoder<Output> {
        return combinators.default(this, value);
    }

    /** Decodes a JSON string. */
    parseJSON(json: string): DecodeResult<Output> {
        try {
            return this.decode(JSON.parse(json));
        } catch (error: any) {
            return Result.error(DecodingError.errorMessage(error.message));
        }
    }
}
